import { alpha } from "@mui/material/styles";
import colors from "./colors";

/**
 * The standard long-form card — a white surface holding a whole form, used by
 * Create Loan and the borrower-link send screens so every multi-question form in
 * the portal sits on the same slab.
 *
 * Spread onto the Box that wraps the form body; pair it with `maxWidth: 880` on
 * the parent so line lengths stay readable on wide screens.
 */
export const FORM_CARD_SX = {
  backgroundColor: "#fff",
  border: `1px solid ${colors.slate200}`,
  borderRadius: 2,
  // borderRadius: 3.5,
  boxShadow: "0 1px 2px rgba(12,27,42,.05),0 8px 24px rgba(12,27,42,.06)",
  // boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
  // p: { xs: 2.5, md: "44px 48px 40px" },
  // No input-radius override here. This used to force 8px "to match the
  // /loan-application inputs", but that form never rendered 8px — it takes the
  // theme's MuiOutlinedInput radius (10px), so the override was the thing
  // creating the mismatch it claimed to fix. The theme is the only source of
  // truth for field radius; see INPUT_BORDER_RADIUS.
};
export const FORM_SECTION_SX = {
  p: { xs: 2.5, md: "36px 36px" },
  // p: { xs: 2.5, md: "26px 30px" },
  // p: { xs: 2.5, md: "44px 48px 40px" },
};

/**
 * Field label metrics, matched to the LOS application form. Spread onto the same
 * card as FORM_CARD_SX so a long form reads identically wherever it appears —
 * the public /loan-application and /credit-auth forms, Create Loan, and the
 * send-a-link screens.
 *
 * Applied per-card rather than baked into RffInputWrapper: the dashboard's
 * denser screens (drilldown panels, modals) still want the tighter default
 * label, and scoping keeps both without a prop on every field.
 *
 * Hooks onto the class names RffInputWrapper puts on its label/helper. Targeting
 * bare `label` would also catch RffBooleanCheckbox's FormControlLabel.
 */
export const FORM_FIELD_SX = {
  // The label's own 3px margin is the whole gap; the flex gap would add to it.
  "& .rff-field-root": { gap: 0 },
  "& .rff-field-label": {
    // fontSize: "0.84375rem",
    // fontWeight: 600,
    // color: colors.slate500,
    fontSize: "12.5px",
    fontWeight: 400,
    lineHeight: 1.4375,
    color: "#0c1b2a",
    whiteSpace: "normal",
    mb: "3px",
  },
  // Required fields are marked in the title navy, not the action color.
  "& .rff-field-label .rff-field-required": { color: colors.slate900 },
  "& .rff-field-helper": { fontStyle: "italic", fontSize: "12px" },
};

/**
 * Grid spacing to pair with FORM_FIELD_SX. The row gap is deliberately much
 * larger than the column gap: it separates whole label-plus-control units, where
 * the column gap only separates two controls sitting side by side.
 */
export const FORM_GRID_COLUMN_GAP = 2.4;
export const FORM_GRID_ROW_GAP = 4.8;

/**
 * Yes/no and single-select toggles rendered as outlined pills rather than MUI's
 * default square segmented buttons. Spread onto the same card as FORM_CARD_SX.
 */
export const TOGGLE_PILL_SX = {
  "& .MuiToggleButtonGroup-root": { gap: "10px" },
  "& .MuiToggleButtonGroup-root .MuiButtonBase-root": {
    borderRadius: "999px !important",
    border: `1px solid ${colors.slate300} !important`,
    px: 2,
    py: 0.9,
    fontSize: 13.5,
    fontWeight: 400,
    textTransform: "none",
    color: colors.slate600,
    "&:hover": { backgroundColor: colors.slate50 },
    "&.Mui-selected": {
      backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
      // LENDMARQ DIVERGENCE: tpo writes these as `"primary.main !important"`.
      // MUI's sx resolves the bare palette path `primary.main`, but appending
      // `!important` makes the string stop matching a palette key, so it is
      // emitted verbatim as invalid CSS and dropped — which left the selected
      // pill with slate text on a slate border and only the faint background
      // tint, reading grey rather than selected. Resolve the color first, then
      // append the flag.
      borderColor: (theme) => `${theme.palette.primary.main} !important`,
      color: (theme) => `${theme.palette.primary.main} !important`,
      fontWeight: 600,
      "&:hover": {
        backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.14),
      },
    },
  },
};
